'use client'
import {
   AiMlAutomation,
   BusinessGrow,
   ContactUsForm,
   FAQs,
   GameChangingIdea,
   TechNServicesHeroSection,
   TemplateSlider,
   WordsOfAppreciation,
} from '@/components'
import { AndroidIcon } from '@/svgs'
import React from 'react'
import CommonTechGrid from '../../Components/CommonTechGrid'
import AndroidFromStrategy from './components/AndroidFromStrategy'
import AndroidWhyChoose from './components/AndroidWhyChoose'

const slides = [
   {
      icon: '/assets/svg/technologies/android/high.svg',
      topText: 'Native Android Development',
      header: 'High-Performance Android Apps',
      details:
         'Build robust, scalable Android applications using Kotlin and Java with optimized UI and seamless functionality.',
   },
   {
      icon: '/assets/svg/technologies/android/stunning.svg',
      topText: 'UI/UX & Animations',
      header: 'Stunning Android Interfaces',
      details:
         'Create smooth, intuitive designs using Jetpack Compose, Material Design, and advanced animations for better engagement.',
   },
   {
      icon: '/assets/svg/technologies/android/api.svg',
      topText: 'API & Cloud Integration',
      header: 'Seamless API Connectivity',
      details:
         'Connect Android apps with RESTful APIs, Firebase, GraphQL, and cloud services for real-time data sync.',
   },
   {
      icon: '/assets/svg/technologies/android/secure.svg',
      topText: 'Security & Data Protection',
      header: 'Secure Android Applications',
      details:
         'Implement authentication, data encryption, biometrics, and secure local storage to protect user data and privacy.',
   },
   {
      icon: '/assets/svg/technologies/android/optimzed.svg',
      topText: 'Performance & Optimization',
      header: 'Optimized App Performance',
      details:
         'Enhance speed with background processing, lazy loading, multithreading, and efficient memory management.',
   },
   {
      icon: '/assets/svg/technologies/android/launch.svg',
      topText: 'Google Play Deployment',
      header: 'Launch & Scale Apps',
      details:
         'Publish Android apps on Google Play with CI/CD automation, app store optimization (ASO), and version control strategies.',
   },
]

const gridContent = [
   {
      title: 'Health & Fitness',
      subtitle: 'Android Applications for healthcare',
      description:
         'Develop secure and HIPAA-compliant healthcare applications for Android. Build real-time patient monitoring apps, AI-powered diagnostics, telemedicine solutions, and integrations with Google Fit & Wear OS for enhanced healthcare accessibility.',
      url: '/assets/svg/technologies/angular/Health.svg',
   },
   {
      title: 'Banking & Finance',
      subtitle: 'Android Applications for Finance & Banking',
      description:
         'Enhance financial services with Android-powered applications. Use secure transactions, AI-based fraud detection, biometric identification (fingerprint & facial recognition), and real-time investment monitoring for hassle-free banking experiences.',
      url: '/assets/svg/technologies/angular/Fintech.svg',
   },
   {
      title: 'Restaurant & Hospitality',
      subtitle: 'Android Applications for Restaurants & Hospitality',
      description:
         'Optimize restaurant operations with Android apps. Develop AI-powered food ordering systems, reservation management apps, POS solutions, and customer loyalty programs for an enhanced dining experience.',
      url: '/assets/svg/technologies/angular/Food.svg',
   },
   {
      title: 'Enhanced eCommerce',
      subtitle: 'Android Applications for Enhanced eCommerce',
      description:
         'Boost e-commerce businesses with Android applications. Enforce AI-based product suggestions, Google Pay support, voice search-capable shopping, and real-time order tracking for easy shopping.',
      url: '/assets/svg/technologies/angular/Ecommerce.svg',
   },
   {
      title: 'Logistics & Supply Chain',
      subtitle: 'Android Applications for Logistics & Supply Chain',
      description:
         'Streamline supply chain management with Android apps. Use real-time shipment tracking, fleet management, AI-based demand forecasting, and automated logistics dashboards for increased efficiency',
      url: '/assets/svg/technologies/angular/Supply.svg',
   },
   {
      title: 'Social Networking',
      subtitle: 'Android Applications for Social Networking',
      description:
         'Develop engaging social media platforms with Android applications. Implement real-time messaging, AI-based content recommendations, live streaming features, and push notifications for maximum user engagement.',
      url: '/assets/svg/technologies/angular/Social.svg',
   },
   {
      title: 'Gaming & Sports Analytics',
      subtitle: 'Android Applications for Gaming & Sports Analytics',
      description:
         'Enhance gaming experiences with Android applications. Create AR/VR-based gameplay, real-time leaderboards, AI-based player analytics, and smooth multiplayer matchmaking to enhance interactivity',
      url: '/assets/svg/technologies/angular/Gaming.svg',
   },
   {
      title: 'Travel & Tourism',
      subtitle: 'Android Applications for Travel & Tourism',
      description:
         'Revolutionize travel experiences with Android applications. Develop AI-facilitated trip planners, GPS navigation, live booking engines, and travel personalized tips for augmented discovery',
      url: '/assets/svg/technologies/angular/travel.svg',
   },
   {
      title: 'Real Estate',
      subtitle: 'Android Applications for Real Estate',
      description:
         'Optimize real estate platforms with Android applications. Enable real-time mortgage calculators, virtual 3D property tours, AI-powered property searching, and property worth estimations.',
      url: '/assets/svg/technologies/angular/Real-Estate.svg',
   },
   {
      title: 'Education & E-Learning',
      subtitle: 'Android Applications for Education & E-Learning',
      description:
         'Transform digital learning experiences with Android apps. Enable AI-powered personalized learning paths, real-time quizzes, offline content access, and interactive video lectures for seamless education.',
      url: '/assets/svg/technologies/angular/Edu.svg',
   },
   {
      title: 'Media & Entertainment',
      subtitle: 'Android Applications for Media & Entertainment',
      description:
         'Enhance digital media engagement with Android applications. Implement AI-driven content recommendations, real-time video streaming, interactive AR/VR features, and immersive entertainment experiences.',
      url: '/assets/svg/technologies/angular/Media.svg',
   },
   {
      title: 'Government & Public Sector',
      subtitle: 'Android Applications for Government & Public Sector',
      description:
         'Modernize public services with Android applications. Develop secure citizen portals, real-time data tracking, AI-powered government chatbots, and digital identity verification systems for enhanced governance.',
      url: '/assets/svg/technologies/angular/Gov.svg',
   },
]

const faqData = [
   {
      question:
         'What is Android app development, and why is it necessary for contemporary businesses?',
      answer:
         'Anyone creating applications for Android must use things like Kotlin and Java. Since Android is used by more than 70% of people worldwide, businesses trying to reach a wide audience must use its platform.',
   },
   {
      question:
         'How does Android app development benefit business growth and outreach?',
      answer:
         'You can reach a worldwide audience with Android apps, alter them to your preferences and offer them globally through both the Google Play Store and third-party sites. They help businesses target users on different gadgets, globally and offer different prices for their services.',
   },
   {
      question:
         'What types of applications are best suited for Android development?',
      answer:
         'Developing eCommerce apps, social networks, educational tools and others means Android is a great option since it provides deep system integration and flexibility.',
   },
   {
      question:
         'What are the key advantages of Android over other mobile platforms?',
      answer:
         'Android can work on many different devices, lets you design UIs freely, uses many Google services, simplifies hardware integration and provides open software tools for affordably expanding.',
   },
   {
      question: 'Is Android development suitable for startups and MVPs?',
      answer:
         'Yes, Android is a good choice for startups thanks to quick deployment, detailed documentation and the affordability and easiness of running MVPs among different types of users.',
   },
   {
      question: 'How secure is Android for app development in 2024?',
      answer:
         'Android can defend itself by offering biometric identification, encryption for different data types, Google Play Protect and safe application programming interfaces. Learning the right development practices will help Android apps satisfy security and compliance regulations.',
   },
   {
      question:
         'How much time is required to complete a native Android application?',
      answer:
         'You can create a basic Android app within 3 to 5 weeks or it may take 2 to 6 months for more advanced apps with new features and an involved design.',
   },
   {
      question:
         'What tools and technologies are commonly used in Android development?',
      answer:
         'You’ll often use Android Studio, Kotlin, Java, Jetpack Compose, Firebase, Retrofit, Room and tools like Gradle and Git for automation and controlling your changes.',
   },
   {
      question:
         'Can Android apps run across different device types and screen sizes?',
      answer:
         'Yes, frameworks for responsive development allow Android apps to adjust for phones, tablets, foldable equipment, TVs and wearable devices to offer the same perfect performance.',
   },
   {
      question:
         'What should I look for in an Android app development team or agency?',
      answer:
         'Select a team of developers who can use Kotlin and Java, know Android SDK and Jetpack, optimize apps for all devices and have a history of publishing hits on the Google Play Store.',
   },
]

function AndroidPage() {
   return (
      <>
         <TechNServicesHeroSection
            title={`Android application Development company`}
            subtitle="Android App Development Services for Scalable & High-Performance Solutions"
            description="Our Android application development solutions design safe, high-performing, and scalable apps. With the use of Kotlin, Java, and the new Android frameworks, we provide effortless usability, engaging UI/UX, and improved performance—fostering increased user interaction and business growth within a competitive mobile-first economy."
            tags={[
               'Customized Android App Development Solutions',
               ' App Development',
               'Scalable and Secure Android Applications',
               'Android Apps',
               'Google Play Store Optimization Services',
            ]}
            HeroIcon={AndroidIcon}
         />
         <BusinessGrow />
         <AiMlAutomation
            amdesc="We utilize our skill in creating high-performance, scalable, and highly featured Android applications with contemporary frameworks. Our proprietary Android solutions provide smooth user experiences, high performance, and flexibility to accommodate changing business needs."
            amsubtitle={'Expert Android Development Solutions'}
            amtitle={'Scalable & Secure Android Development Services'}
            slides={slides}
         />
         <CommonTechGrid
            gridTitle={'Android Application-Powered Digital Innovation:'}
            gridSubTitle={'Scalable Solutions for Every Industry'}
            gridTechbody={
               'Empowering Businesses with Modern Android Applications'
            }
            gridDesc={
               'We at Benzatine are Android app development specialists, creating high-performance, secure, and intuitive apps for smartphones, tablets, wearables, and smart TVs. Our solutions are based on cutting-edge technologies such as Kotlin, Java, Jetpack Compose, Firebase, and AI integrations to provide smooth user experiences, strong functionalities, and business success.'
            }
            gridContent={gridContent}
         />
         <AndroidFromStrategy />
         <AndroidWhyChoose />
         <GameChangingIdea />

         <section className="template-slider-section page_top_section section_secondary_bg">
            <div className="container">
               <h4 className="section_heading_title tech_title our-successfull-section">
                  Our Successful Web Application Development Stories
               </h4>
               <p className="features_description m-auto mb-4">
                  Innovative solutions that transformed businesses and delivered
                  real results.
               </p>
            </div>
            <div>
               <TemplateSlider />
            </div>
         </section>

         <section className="section appreciation-slider-section page_top_section section_secondary_bg pt-0">
            <WordsOfAppreciation />
         </section>

         <FAQs faqsConst={faqData} />
         <ContactUsForm />
      </>
   )
}

export default AndroidPage
